<template>
  <div>
    <v-date-picker
      v-model="dateValue"
      no-title
      :min="minDate"
      :first-day-of-week="params.firstDayOfWeek"
      :locale="params.locale"
      :picker-date="pickerDate"
      :range="isRangeMode"
      show-current
      @input="handleDateInput"
    >
      <v-spacer />
      <!-- Render buttons dynamically based on the actions prop -->
      <v-btn
        v-for="action in params.actions"
        :key="action.label"
        text
        outlined
        depressed
        small
        color="primary"
        @click="action.callback"
      >
        {{ action.label }}
      </v-btn>
    </v-date-picker>
  </div>
</template>

<script>
import Vue from 'vue';
import { get, isEqual, cloneDeep, isEmpty } from 'lodash';
import moment from 'moment';
import { yearMonthDayFormat } from '@enums/date-formats';

export default Vue.extend({
  data() {
    return {
      dateValue: null,
      isRangeMode: false,
      originalDateValue: null, // Store the original date to compare changes
    };
  },

  computed: {
    minDate() {
      return this.params.minDate(this.params);
    },

    pickerDate() {
      if (this.params.pickerDate) return this.params.pickerDate(this.params);
      return undefined;
    },

    shouldAutoSelectToday() {
      return get(this.params, 'autoSelectToday', false);
    },

    todayDate() {
      return moment().format(yearMonthDayFormat);
    },
  },
  created() {
    this.isRangeMode = get(this.params, 'range', false);
  },

  mounted() {
    // Initialize the value based on the mode
    if (this.isRangeMode) {
      let initialValue;

      // Check if params.value is an array (new format) or object (old format)
      if (Array.isArray(this.params.value)) {
        // New format - directly use the array
        initialValue = this.params.value.length === 2 ? this.params.value : [];
      } else {
        // Legacy format - extract from object
        const startDate = get(this.params.value, 'start', null);
        const endDate = get(this.params.value, 'end', null);

        if (startDate && endDate) {
          initialValue = [startDate, endDate];
        } else {
          initialValue = [];
        }
      }

      // Auto-select today's date in range mode if enabled and no dates are selected
      if (this.shouldAutoSelectToday && isEmpty(initialValue)) {
        this.dateValue = [this.todayDate];
      } else {
        this.dateValue = initialValue;
      }
    } else {
      // Single date mode - use as before
      this.dateValue = this.params.value;
    }

    // Store original value for change detection
    this.originalDateValue = cloneDeep(this.dateValue);
  },

  methods: {
    handleDateInput() {
      // For range mode, only close when both dates are selected
      if (this.isRangeMode) {
        if (this.dateValue && this.dateValue.length === 2) {
          // Sort dates to ensure start < end
          const sortedDates = [...this.dateValue].sort();
          this.dateValue = sortedDates;

          // Add slight delay to ensure value is set
          setTimeout(() => {
            this.params.api.stopEditing();
          }, 200);
        }
      } else {
        // Single date mode - close immediately
        this.params.api.stopEditing();
      }
    },

    /* Component Editor Lifecycle methods */
    getValue() {
      if (this.isRangeMode) {
        // Range mode - return object with start/end
        if (!this.dateValue || this.dateValue.length !== 2) {
          return null;
        }

        // Check if values have changed
        const dateHasChanged = !isEqual(this.originalDateValue, this.dateValue);

        return {
          start: this.dateValue[0],
          end: this.dateValue[1],
          dateHasChanged,
        };
      }
      // Single date mode - use existing logic
      return this.params.dateValue(this.dateValue);
    },

    isCancelBeforeStart() {
      return false;
    },

    isCancelAfterEnd() {
      if (this.isRangeMode) {
        return false; // No validation for range mode
      }
      // Existing validation for single date mode
      return this.dateValue > 1000;
    },
    reset() {
      this.dateValue = [];
    },
  },
});
</script>

<style lang="scss" scoped></style>
